import { Currency } from '@prisma/client';
import { TSetNotification } from '../random';

// Enums of all the products that we currently sell.
// They dont come from any billing provider.
// If we add any new products internally, we should add them here.
// This excludes the marketplace service products as they are handled dynamically.
export enum BillingProductsEnum {
  // Compliance basic plans
  COMPLIANCE_BASIC_IN = 'COMPLIANCE_BASIC_IN',
  COMPLIANCE_BASIC_SG = 'COMPLIANCE_BASIC_SG',
  COMPLIANCE_BASIC_UK = 'COMPLIANCE_BASIC_UK',
  COMPLIANCE_BASIC_US = 'COMPLIANCE_BASIC_US',
  COMPLIANCE_BASIC_CA = 'COMPLIANCE_BASIC_CA',
  COMPLIANCE_BASIC_IE = 'COMPLIANCE_BASIC_IE',

  // Compliance pro plans
  COMPLIANCE_PRO_IN = 'COMPLIANCE_PRO_IN',
  COMPLIANCE_PRO_SG = 'COMPLIANCE_PRO_SG',
  COMPLIANCE_PRO_UK = 'COMPLIANCE_PRO_UK',
  COMPLIANCE_PRO_US = 'COMPLIANCE_PRO_US',

  // US Registered Agent Plan
  US_REGISTERED_AGENT_SERVICE_PLAN = 'US_REGISTERED_AGENT_SERVICE_PLAN',

  // Blank PLan for marketplace service
  MARKETPLACE = 'MARKETPLACE',

  // Transfer Pricing
  TRANSFER_PRICING_BASIC = 'TRANSFER_PRICING_BASIC',

  // Sales Tax
  US_SALES_TAX_BASIC = 'US_SALES_TAX_BASIC',
  US_SALES_TAX_PRO = 'US_SALES_TAX_PRO',
  US_SALES_TAX_BASIC_TRANSACTIONS = 'US_SALES_TAX_BASIC_TRANSACTIONS',
  US_SALES_TAX_PRO_TRANSACTIONS = 'US_SALES_TAX_PRO_TRANSACTIONS',

  // Others
  US_REGISTERED_AGENT = 'US_REGISTERED_AGENT',
  US_STATE_FILINGS = 'US_STATE_FILINGS',
  US_VIRTUAL_ADDRESS = 'US_VIRTUAL_ADDRESS',
  LLP_ADDRESS_CHANGE = 'LLP_ADDRESS_CHANGE',
  ENTITY_REINSTATEMENT = 'ENTITY_REINSTATEMENT',
  BOIR_FILINGS = 'BOIR_FILINGS',
  US_APOSTILLE_AND_NOTARIZATION = 'US_APOSTILLE_AND_NOTARIZATION',
  INDIAN_VIRTUAL_ADDRESS = 'INDIAN_VIRTUAL_ADDRESS',
  FDI = 'FDI',
  ODI = 'ODI',

  // Incorporation
  IN_LLP_INCORPORATION = 'IN_LLP_INCORPORATION',
  IN_PVT_LTD_INCORPORATION = 'IN_PVT_LTD_INCORPORATION',
  IR_LTD_INCORPORATION = 'IR_LTD_INCORPORATION',
  SG_PVT_LTD_INCORPORATION = 'SG_PVT_LTD_INCORPORATION',
  UAE_FREE_ZONE_INCORPORATION = 'UAE_FREE_ZONE_INCORPORATION',
  UK_LTD_INCORPORATION = 'UK_LTD_INCORPORATION',
  US_C_CORP_INCORPORATION = 'US_C_CORP_INCORPORATION',
  US_LLC_INCORPORATION = 'US_LLC_INCORPORATION',
}

export enum BillingIntervalEnum {
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
  SEMI_ANNUAL = 'SEMI_ANNUAL',
  YEAR = 'YEAR',
}

export enum BillingProductTypeEnum {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export enum InternalBillingSource {
  NEEDS_ASSESSMENT_CHECKOUT = 'NEEDS_ASSESSMENT_CHECKOUT',
  COMPLIANCE_CHECKOUT = 'COMPLIANCE_CHECKOUT',
  TRANSFER_PRICING_CHECKOUT = 'TRANSFER_PRICING_CHECKOUT',
  MARKETPLACE_SERVICE_CHECKOUT = 'MARKETPLACE_SERVICE_CHECKOUT',
  ENTERPRISE_CHECKOUT = 'ENTERPRISE_CHECKOUT',
}

// This type is used for denoting a billing item in UI.
export type TBillableProduct = TBillingProductForCheckout & {
  quantity: number;
  customUnitPrice?: number;
  totalPriceBeforeDiscount: number;
  totalPriceAfterDiscount: number;
  customizable?: boolean;
  minimumQuantity?: number;
};

export type TBillingContextValue = {
  internalBillingSource: InternalBillingSource;
  billingItems: TBillableProduct[];
  setBillingItems: (billingItems: TBillableProduct[]) => void;
  subTotal: number;
  currency: Currency;
  setNotification: TSetNotification;
  onUnitChange: (productId: string, newQuantity: number) => void;
  onCheckout: () => Promise<boolean>;
  getProductDescription: (billingItem: TBillableProduct) => React.ReactNode;
  shouldBillToClient?: boolean;
  setShouldBillToClient?: (value: boolean) => void;
  isAffiliateFirmUser?: boolean;
};

export type TBillingProduct = {
  id: string;
  title: string;
  description?: string;
  type: BillingProductTypeEnum; // ONE_TIME or RECURRING
};

// the price is stored in the lowest denomination of the currency.
// for example, if the currency is USD, the price is stored in cents.
// if the currency is INR, the price is stored in paise.
export type TBillingProductPrice = {
  productId: string;
  price: number;
  currency: Currency;
  recurring?: {
    interval: BillingIntervalEnum;
    intervalCount: number;
  };
};

// This is the type for items coming from the API Response.
export type TBillingProductForCheckout = TBillingProduct & {
  netPrice: TBillingProductPrice;
};

export type TBillingItemInitialState = {
  quantity: number;
  customizable?: boolean;
  minimumQuantity?: number;
};

export type TBillingProductForCheckoutResponse = Response & {
  json: () => Promise<TBillingProductForCheckout[]>;
};

export type TBillingLineItem = {
  productId: string;
  quantity: number;
  billingInterval: BillingIntervalEnum;
};

// In case of marketplace service, the productId is the vendor product pricing variant id.
export type TCheckoutSessionMetadata = {
  purchaseDetails: {
    productId: string;
    entityAssignments: {
      entityId: string;
      fiscalYear?: number;
    }[];
  }[];
};

export type TRetrieveCheckoutSession = {
  checkoutSessionId: string;
  status: ExternalPaymentStatus;
  metadata: TCheckoutSessionMetadata;
};

// Backend payment provider status - what comes from your payment provider
export enum ExternalPaymentStatus {
  PENDING = 'PENDING', // Payment is being processed
  PAID = 'PAID', // Payment was successful
  ACKNOWLEDGED = 'ACKNOWLEDGED', // Transaction complete (payment + fulfillment)
  FAILED = 'FAILED', // Payment failed
}

// Frontend user-facing status - combines payment and order fulfillment
export enum PaymentStatus {
  // Initial status
  LOADING = 'loading', // Initial loading state and waiting for payment confirmation

  PAYMENT_SUCCESS = 'payment_success', // Payment successful, before resource creation
  PAYMENT_FAILED = 'payment_failed', // Payment failed, fail directly.

  // Fulfillment states
  FULFILLMENT_FAILED = 'fulfillment_failed', // Resource creation failed, but payment succeeded
  FULFILLMENT_PROCESSED = 'fulfillment_processed', // Everything successful (payment + fulfillment)

  // Final states
  CONNECTION_TIMEOUT = 'connection_timeout', // Polling timed out
  MAX_RETRIES_REACHED = 'max_retries_reached', // Maximum number of retries reached
}

export type TBillingContactInfo = {
  name: string;
  email: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  };
  paymentMethods: Array<{
    id: string;
    type: string;
    last4: string;
    brand: string;
    expiryMonth: number;
    expiryYear: number;
    isDefault: boolean;
  }>;
};

export enum BillingPeriodUnit {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export type BillingPeriod = {
  unit: BillingPeriodUnit;
  quantity: number;
};
